import { Injectable } from '@angular/core';
import { ShareRecommendationData } from '@app/core/models/share-recommendation-data';
import { EmailPopinType, Helper, MeasurementCodeType, POLetterFrom, Programs, Tool } from '@app/shared/utils';
import { buildRecommendation } from '@app/shared/utils/static-helpers/consultation-helper';
import { AppState } from '@app/store';
import { selectSelectedPackIds, selectSelectedProducts } from '@app/store/products/products.selectors';
import { environment } from '@env/environment';
import { translateKey } from '@app/shared/utils/static-helpers/translate';
import { selectConsultation, selectConsultationRenalDetectPrediction } from '@app/store/consultation/consultation.selectors';
import { selectUrl } from '@app/store/core';
import { setShareRecommendation } from '@app/store/share-recommendation';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, switchMap, withLatestFrom } from 'rxjs/operators';
import { shareAllowanceRecommendation } from '../actions/allowance-share.actions';
import { selectAllowanceCurrentProgram } from '../selectors/allowance-program.selectors';
import { NutritionRecommendation, Prediction, Product } from '@app/core/models';
import { ConsultationState } from '@app/store/consultation';
import { VetFacade } from '@app/store/vet';
import { SmartRecoStep } from '@app/pages/smart-reco/smart-reco-step';
import { setSmartRecoStep } from '@app/pages/smart-reco/store/actions/smart-reco-steps.actions';
import { setWeightManagementStep } from '@app/pages/weight-management/store/actions/weight-management-steps.actions';
import { WeightManagementStep } from '@app/pages/weight-management/weight-management-step';
import { selectAllowance, selectCurrentRecommendation } from '../selectors/allowance.selectors';
import { AllowanceState } from '../allowance.state';
import { selectIsPetInLactation } from '../selectors/allowance-params.selectors';

@Injectable()
export class AllowanceShareEffects {
  /**
   * Share ration or weight manangement recommendation by email
   */
  public shareAllowanceRecommendation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(shareAllowanceRecommendation),
        withLatestFrom(
          this.store$.select(selectConsultation),
          this.store$.select(selectConsultationRenalDetectPrediction),
          this.store$.select(selectAllowance),
          this.store$.select(selectAllowanceCurrentProgram),
          this.store$.select(selectCurrentRecommendation),
          this.store$.select(selectSelectedPackIds),
          this.store$.select(selectSelectedProducts),
          this.store$.select(selectIsPetInLactation),
          this.store$.select(selectUrl),
          this.vetFacade.currentBigMeasurementUnit$,
        ),
        switchMap(
          ([
            { value: poLetterFrom },
            { patient, lastConsultation, currentConsultation },
            prediction,
            { allowanceParams: params },
            program,
            nutritionRecommendation,
            selectedPackIds,
            products,
            isLactation,
            currentUrl,
            currentBigMeasurementUnit,
          ]: [
            { value: POLetterFrom },
            ConsultationState,
            Prediction,
            AllowanceState,
            Programs,
            NutritionRecommendation,
            string[],
            Product[],
            boolean,
            string,
            currentBigMeasurementUnit: MeasurementCodeType,
          ]) => {
            const data: ShareRecommendationData = {
              actions: !environment.isStandalone && {
                label: params.rationPrimaryActionLabel,
                saveParams: {
                  canSaveOnExit: params.canSaveOnExit,
                  createConsultationBeforeRedirectStep: params.createConsultationBeforeRedirectStep,
                },
              },
              sendBackDataAction: environment.isStandalone &&
                environment.isShowSendBackDataButton && {
                  label: translateKey('sr_sendback_button'),
                },
              backAction: {
                label: translateKey('sr-result-back'),
                backUrl: currentUrl,
              },
              defaultEmail: patient?.contact?.email,
              patientId: patient?.id,
              emailPopintype: EmailPopinType.Recommendation,
              petInfo: params.deprecatedPetInfo,
              poLetterFrom,
              currentConsultation:
                poLetterFrom === POLetterFrom.FlowAllowanceWeightBlock
                  ? currentConsultation
                  : {
                      date: new Date(),
                      prediction,
                    },
              prevConsultation: poLetterFrom === POLetterFrom.FlowAllowanceRationBlock ? lastConsultation : undefined,
              tool: params.tool,
              productRecommendation: buildRecommendation(products, selectedPackIds, nutritionRecommendation, isLactation),
              program: Helper.getProgramTexts(program),
              idealBodyWeight: { measure: params.deprecatedPetInfo?.IBW, measureUnit: currentBigMeasurementUnit },
              owner: patient?.contact,
              recommendationsCriterias: params.criterias,
            };

            if (params.tool === Tool.SmartReco) {
              this.store$.dispatch(setSmartRecoStep({ value: SmartRecoStep.ShareReco }));
            }

            if (params.tool === Tool.WeightManagement) {
              this.store$.dispatch(setWeightManagementStep({ value: WeightManagementStep.ShareReco }));
            }

            return [setShareRecommendation({ data })];
          },
        ),
        /**
         * Errors are handled directly in the popin component
         */
        catchError((err) => {
          console.error(err);
          return of(err);
        }),
      ),
    { dispatch: true },
  );

  constructor(
    private readonly actions$: Actions,
    private readonly store$: Store<AppState>,
    private readonly vetFacade: VetFacade,
  ) {}
}
